/**
 * 공개 프로필(@username)이 서버 데이터를 화면 모델로 바꾸는 방식.
 * 현재 사용처는 `pages/Public/Profile.svelte` 한 곳이다.
 *
 * 무엇이 보이는지(비공개·예약·만료·internal)의 판정은 여기 없다 — 서버가 내린다.
 * 이 모듈은 이미 걸러진 데이터를 받아 묶고 정렬만 한다.
 *
 * 편집기 미리보기는 이 모듈을 쓰지 않는다. `/@:username` 을 iframe 으로 실제 렌더해
 * 같은 문서를 그대로 띄운다 (`.planning/profile-visibility.md` 결정 3).
 */

export type PublicViewLink = {
  title: string;
  description?: string | null;
  type: 'post' | 'external' | 'file';
  url: string;
  emoji?: string | null;
  thumbnailUrl?: string | null;
  clicks?: number | null;
  date?: string | null;
};

export type PublicViewProfile = {
  displayName: string;
  username: string;
  /**
   * 🔴 소속·부서·팀 세 항목을 서버가 공개 규칙대로 " / " 로 합쳐 준 **한 문자열**이다
   * (`User#public_department_label`). 프론트에서 다시 조립하면 술어가 두 벌이 되므로
   * `team` 을 따로 받지 않는다 — 켜진 항목만 이 문자열 안에 이미 들어 있다.
   */
  department: string;
  bio: string;
  avatarInitial: string;
  isVerified: boolean;
  role: string;
  stats: { posts: number; saves: number; presentations: number };
};

/** 서버가 내려주는 링크 한 건 (공개 페이지 / 편집기 payload 공통 필드만). */
export type PublicLinkSource = {
  id?: string;
  title: string;
  description?: string | null;
  url: string;
  click_redirect_url?: string | null;
  emoji?: string | null;
  thumbnail_url?: string | null;
  link_type?: string | null;
  clicks_count?: number | null;
  link_group_id?: string | null;
};

/** 서버가 내려주는 그룹 한 건. `links` 가 없으면 평면 목록에서 소속 링크를 찾는다. */
export type PublicGroupSource = {
  id: string;
  title: string;
  emoji?: string | null;
  is_collapsed?: boolean;
  links?: PublicLinkSource[];
};

export type PublicProfileSource = {
  display_name?: string | null;
  username?: string | null;
  /** 서버 합성 문자열(소속 / 부서 / 팀). 개별 필드가 아니다. */
  department?: string | null;
  bio?: string | null;
  avatar_initial?: string | null;
  is_verified?: boolean | null;
  role?: string | null;
  stats?: {
    posts_count?: number | null;
    followers_count?: number | null;
    presentations_count?: number | null;
  } | null;
};

/**
 * 공개 프로필 헤더가 쓰는 화면 모델.
 * 비공개로 꺼진 항목은 서버가 키를 빼고 보내므로, 여기서는 빈 값으로 떨어진다.
 */
export function toViewProfile(profile: PublicProfileSource): PublicViewProfile {
  return {
    displayName: profile.display_name || '',
    username: profile.username || '',
    department: profile.department || '',
    bio: profile.bio || '',
    avatarInitial: profile.avatar_initial || profile.display_name?.[0] || '?',
    isVerified: profile.is_verified || false,
    role: profile.role || 'user',
    stats: {
      posts: profile.stats?.posts_count || 0,
      saves: profile.stats?.followers_count || 0,
      presentations: profile.stats?.presentations_count || 0,
    },
  };
}

/**
 * 그룹 링크(그룹 이름을 설명 앞에 붙임) 다음 미분류 링크 순서의 평면 목록.
 * 접힌 그룹은 통째로 빠지고, 그룹이 하나도 없으면 전체 링크를 순서대로 그린다.
 */
export function buildPublicLinkList(options: {
  links: PublicLinkSource[];
  groups?: PublicGroupSource[];
  isGroupCollapsed?: (group: PublicGroupSource) => boolean;
}): PublicViewLink[] {
  const {
    links,
    groups = [],
    isGroupCollapsed = (group: PublicGroupSource) => Boolean(group.is_collapsed),
  } = options;

  if (groups.length === 0) return links.map(toViewLink);

  const arranged: PublicLinkSource[] = [];

  for (const group of groups) {
    if (isGroupCollapsed(group)) continue;

    const groupLinks = group.links ?? links.filter((link) => link.link_group_id === group.id);
    const prefix = group.emoji ? `${group.emoji} ${group.title}` : group.title;

    for (const link of groupLinks) {
      arranged.push({
        ...link,
        description: link.description ? `${prefix} · ${link.description}` : prefix,
      });
    }
  }

  // 그룹에 속하지 않은 링크는 뒤에 붙는다.
  // 그룹이 있는데 목록에 없는 링크(=비공개 그룹 소속)는 어느 쪽에도 들어가지 않는다.
  arranged.push(...links.filter((link) => !link.link_group_id));

  return arranged.map(toViewLink);
}

function toViewLink(link: PublicLinkSource): PublicViewLink {
  return {
    title: link.title,
    description: link.description,
    type: (link.link_type as PublicViewLink['type']) || 'external',
    url: link.click_redirect_url || link.url,
    emoji: link.emoji,
    thumbnailUrl: link.thumbnail_url,
    clicks: link.clicks_count,
  };
}
